import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { Bell, Check, X, Calendar, MessageCircle, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const KIND_META = {
  schedule_request: { icon: Calendar, color: 'text-indigo-600 bg-indigo-100' },
  schedule_approve: { icon: Check, color: 'text-emerald-600 bg-emerald-100' },
  schedule_reject: { icon: X, color: 'text-red-600 bg-red-100' },
  schedule_suggest: { icon: MessageCircle, color: 'text-amber-600 bg-amber-100' },
  schedule_cancelled: { icon: X, color: 'text-slate-500 bg-slate-100' },
};

/**
 * Top-nav notification bell. Fetches scheduler notifications every 30s,
 * shows unread badge, dropdown with recent + mark-all-read.
 */
export default function NotificationBell({ role = 'student' }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/scheduler/notifications', { params: { limit: 15 } });
      setItems(res.data.notifications || []);
      setUnread(res.data.unread || 0);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const markRead = async (id) => {
    try {
      await api.post(`/scheduler/notifications/${id}/read`);
      setItems(items.map(n => n.id === id ? { ...n, read: true } : n));
      setUnread(Math.max(0, unread - 1));
    } catch (_e) { /* silent */ }
  };
  const markAll = async () => {
    try {
      await api.post('/scheduler/notifications/read-all');
      setItems(items.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch (_e) { /* silent */ }
  };
  const handleClick = async (n) => {
    if (!n.read) await markRead(n.id);
    setOpen(false);
    if (n.link) {
      const dashUrl = role === 'teacher' ? '/teacher-dashboard' : role === 'admin' ? '/admin-dashboard' : '/student-dashboard';
      navigate(n.link.startsWith('/') ? dashUrl : `/${dashUrl}`);
    }
  };

  return (
    <div className="relative" ref={ref} data-testid="notification-bell">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="relative p-2 rounded-full hover:bg-slate-100 transition"
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-slate-700" />
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[420px] bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden z-50" data-testid="notification-dropdown">
          <div className="px-3 py-2 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-slate-100 flex items-center justify-between">
            <p className="text-xs font-bold text-slate-900">Notifications</p>
            {unread > 0 && (
              <button onClick={markAll} className="text-[10px] text-indigo-600 hover:text-indigo-800 font-bold" data-testid="mark-all-read">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {loading && items.length === 0 && <p className="text-xs text-slate-400 text-center py-6">Loading…</p>}
            {!loading && items.length === 0 && (
              <div className="flex flex-col items-center py-8 text-slate-400">
                <Info className="w-6 h-6 mb-1" />
                <p className="text-xs">No notifications yet</p>
              </div>
            )}
            {items.map(n => {
              const meta = KIND_META[n.kind] || { icon: Bell, color: 'text-slate-500 bg-slate-100' };
              const Icon = meta.icon;
              return (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-slate-50 border-b border-slate-100 transition ${!n.read ? 'bg-indigo-50/40' : ''}`}
                  data-testid={`notif-item-${n.id}`}
                >
                  <div className={`w-8 h-8 rounded-full ${meta.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs ${!n.read ? 'font-bold text-slate-900' : 'font-semibold text-slate-700'} truncate`}>{n.title}</p>
                    {n.body && <p className="text-[11px] text-slate-500 truncate">{n.body}</p>}
                    <p className="text-[9px] text-slate-400 mt-0.5">{(n.created_at || '').slice(0, 16).replace('T', ' ')}</p>
                  </div>
                  {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
